<template>
  <div class="api-clients-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-key"></i>
        Clientes API
      </h1>
      <p class="page-subtitle">Gerencie os clientes que utilizam a API de integração de loteria</p>
    </div>

    <!-- Navegação por tabs -->
    <div class="tabs-container">
      <nav class="modern-tabs">
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 1 }" 
          @click="selectedTab = 1"
        >
          <i class="fa-solid fa-list"></i>
          LISTAGEM
        </button>
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 2 }" 
          @click="editingClient ? selectedTab = 2 : createClient()"
        >
          <i class="fa-solid fa-plus tab-plus-icon"></i>
          {{ editingClient ? 'EDITAR' : 'CADASTRO' }}
        </button>
      </nav>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Tab 1: Listagem -->
    <div v-show="selectedTab === 1 && !loading" class="tab-content">
      <div class="table-card">
        <!-- Cabeçalho da tabela -->
        <div class="table-header">
          <div class="search-container">
            <div class="search-input-group">
              <i class="fa-solid fa-search search-icon"></i>
              <input 
                type="text" 
                v-model="quickFilter" 
                class="search-input" 
                placeholder="Pesquisar clientes..." 
                autocomplete="off"
              />
            </div>
          </div>
          <button 
            type="button" 
            class="btn-primary" 
            @click="createClient()"
          >
            <i class="fa-solid fa-plus"></i>
            Adicionar Cliente
          </button>
        </div>

        <!-- Filtros -->
        <div class="filters-section">
          <div class="filter-group">
            <label>Status:</label>
            <select v-model="filters.active" class="filter-select">
              <option value="">Todos</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
          <div class="filter-actions">
            <button class="btn-secondary" @click="searchClients">
              <i class="fa-solid fa-search"></i>
              Buscar
            </button>
            <button class="btn-secondary" @click="clearFilters">
              <i class="fa-solid fa-times"></i>
              Limpar
            </button>
          </div>
        </div>

        <!-- Tabela -->
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th style="text-align: center;">Logo</th>
                <th style="text-align: center;">Nome</th>
                <th style="text-align: center;">Domínio</th>
                <th style="text-align: center;">Regiões</th>
                <th style="text-align: center;">Loterias</th>
                <th style="text-align: center;">API Key</th>
                <th style="text-align: center;">Status</th>
                <th style="text-align: center;">Criado em</th>
                <th style="text-align: center;" width="140">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="client in filteredClients" 
                :key="client.id"
                class="table-row"
              >
                <td style="text-align: center;" class="logo-column">
                  <div class="client-logo">
                    <img 
                      v-if="client.logoUrl" 
                      :src="client.logoUrl" 
                      :alt="client.name"
                      class="logo-image"
                      @error="handleImageError"
                    />
                    <div v-else class="logo-placeholder">
                      <i class="fa-solid fa-image"></i>
                    </div>
                  </div>
                </td>
                <td style="text-align: center;" class="name-column">
                  <div class="client-info">
                    <span class="client-name">{{ client.name }}</span>
                  </div>
                </td>
                <td style="text-align: center;">
                  <a :href="'https://' + client.domain" target="_blank" class="domain-link">
                    {{ client.domain }}
                    <i class="fa-solid fa-external-link-alt"></i>
                  </a>
                </td>
                <td style="text-align: center;">
                  <span class="scope-badge">{{ formatScopeCount(client.allowedRegionIds, 'região', 'regiões', 'Acesso total') }}</span>
                </td>
                <td style="text-align: center;">
                  <span class="scope-badge">{{ formatScopeCount(client.allowedLotteryDetailIds, 'loteria', 'loterias', 'Acesso total') }}</span>
                </td>
                <td style="text-align: center;">
                  <div class="api-key-cell">
                    <code class="api-key-preview">{{ client.apiKey ? (client.apiKey.substring(0, 20) + '...') : '-' }}</code>
                    <button 
                      v-if="client.apiKey"
                      type="button" 
                      class="btn-icon" 
                      @click="copyApiKey(client.apiKey)"
                      title="Copiar API Key"
                    >
                      <i class="fa-solid fa-copy"></i>
                    </button>
                    <button 
                      v-if="client.apiKey"
                      type="button" 
                      class="btn-icon" 
                      @click="showApiKey(client)"
                      title="Ver API Key completa"
                    >
                      <i class="fa-solid fa-eye"></i>
                    </button>
                  </div>
                </td>
                <td style="text-align: center;">
                  <span :class="['status-badge', client.active ? 'badge-success' : 'badge-danger']">
                    {{ client.active ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td style="text-align: center;">{{ formatDate(client.createdAt) }}</td>
                <td style="text-align: center;">
                  <div class="action-buttons">
                    <button 
                      class="btn-action btn-edit" 
                      @click="editClient(client)"
                      title="Editar"
                    >
                      <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button 
                      class="btn-action btn-regenerate" 
                      @click="regenerateKeys(client)"
                      title="Regenerar chaves"
                    >
                      <i class="fa-solid fa-key"></i>
                    </button>
                    <button 
                      class="btn-action btn-delete" 
                      @click="deleteClient(client)"
                      title="Excluir"
                    >
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredClients.length === 0" class="no-data">
                <td colspan="9">
                  <div class="no-data-message">
                    <i class="fa-solid fa-inbox"></i>
                    <span>Nenhum cliente encontrado</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Tab 2: Formulário -->
    <div v-show="selectedTab === 2" class="tab-content">
      <div class="form-card">
        <div class="form-header">
          <h3 class="form-title">
            <i class="fa-solid fa-key"></i>
            {{ editingClient ? 'Editar Cliente API' : 'Novo Cliente API' }}
            <p class="form-subtitle">
              {{ editingClient ? 'Atualize as informações do cliente' : 'Preencha as informações do novo cliente' }}
            </p>
          </h3>
        </div>

        <div class="form-body">
          <div class="form-grid">
            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-user"></i>
                Nome do Cliente
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="form.name"
                placeholder="Digite o nome do cliente..."
              />
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-globe"></i>
                Domínio
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="form.domain"
                placeholder="exemplo.com"
                pattern="[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}"
                title="Digite um domínio válido (ex: exemplo.com)"
              />
              <small class="form-help-text">
                <i class="fa-solid fa-info-circle"></i>
                Digite apenas o domínio, sem http:// ou https://
              </small>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-toggle-on"></i>
                Status
              </label>
              <select v-model="form.active" class="form-input">
                <option :value="true">Ativo</option>
                <option :value="false">Inativo</option>
              </select>
            </div>

            <div class="input-group full-width">
              <label class="input-label">
                <i class="fa-solid fa-map-location-dot"></i>
                Regiões permitidas
              </label>
              <div class="scope-quick-actions">
                <button
                  type="button"
                  class="btn-secondary"
                  @click="selectOnlyOnlineRegion"
                >
                  <i class="fa-solid fa-globe"></i>
                  Usar só região online
                </button>
              </div>
              <div class="multiselect-container">
                <MultiSelect
                  v-model="selectedRegions"
                  :options="availableRegions"
                  placeholder="Digite online, banca ou região..."
                  :showSelectAllButtons="true"
                  :showSelectedBelow="true"
                />
              </div>
              <small class="form-help-text">
                <i class="fa-solid fa-info-circle"></i>
                A banca/região online aparece no topo (mesmo se estiver oculta no header). Vazio = todas as regiões.
              </small>
            </div>

            <div class="input-group full-width">
              <label class="input-label">
                <i class="fa-solid fa-location-crosshairs"></i>
                Região padrão das apostas
              </label>
              <select v-model="form.defaultRegionId" class="form-input">
                <option
                  v-for="region in defaultRegionOptions"
                  :key="region.id"
                  :value="region.id"
                >
                  {{ region.name }}
                </option>
              </select>
              <small class="form-help-text">
                <i class="fa-solid fa-info-circle"></i>
                Não escolha a opção que começa com <b>ONLINE —</b> se quiser descarga.
                Escolha a região normal (ex.: <b>MasterBixo — … · com descarga</b>).
                Sellers WEB_CLIENTE nascem nesta região.
              </small>
            </div>

            <div class="input-group full-width">
              <label class="input-label">
                <i class="fa-solid fa-ticket"></i>
                Loterias / horários permitidos
              </label>
              <div class="multiselect-container">
                <MultiSelect
                  v-model="selectedLotteryDetails"
                  :options="availableLotteryDetails"
                  placeholder="Selecione as loterias (vazio = acesso total)"
                  :showSelectAllButtons="true"
                  :showSelectedBelow="true"
                />
              </div>
              <small class="form-help-text">
                <i class="fa-solid fa-info-circle"></i>
                Se nenhuma loteria for selecionada, o cliente verá bilhetes de todas as loterias. Ao selecionar um horário (ex.: PT-SUL 14h), todos os horários da mesma loteria ficam liberados.
              </small>
            </div>

            <div class="input-group full-width">
              <label class="input-label">
                <i class="fa-solid fa-image"></i>
                URL da Logo
              </label>
              <input 
                class="form-input" 
                type="url" 
                v-model="form.logoUrl"
                placeholder="https://exemplo.com/logo.png"
              />
              <small class="form-help-text">
                <i class="fa-solid fa-info-circle"></i>
                URL da imagem da logo que será exibida nos bilhetes do cliente
              </small>
              <div v-if="form.logoUrl" class="logo-preview">
                <img 
                  :src="form.logoUrl" 
                  alt="Preview da logo"
                  class="preview-image"
                  @error="handlePreviewError"
                />
              </div>
            </div>

            <div class="input-group full-width">
              <label class="input-label">
                <i class="fa-solid fa-wallet"></i>
                Webhook da carteira (débito ANTES da aposta)
              </label>
              <input
                class="form-input"
                type="url"
                v-model="form.walletWebhookUrl"
                placeholder="https://api.agregador.com/api/ralph/webhook/bet"
              />
              <small class="form-help-text">
                <i class="fa-solid fa-info-circle"></i>
                POST server-to-server com { betId, sellerId, totalAmount, currency }. Se status != 1 a aposta não é gravada. Vazio = carteira local.
              </small>
            </div>

            <div class="input-group full-width">
              <label class="input-label">
                <i class="fa-solid fa-flag-checkered"></i>
                Webhook de resultado / cancelamento
              </label>
              <input
                class="form-input"
                type="url"
                v-model="form.resultWebhookUrl"
                placeholder="https://api.agregador.com/api/ralph/webhook/result"
              />
              <small class="form-help-text">
                <i class="fa-solid fa-info-circle"></i>
                POST { betId, sellerId, status: won|lost|canceled, prizeValue }. Funciona com o iframe fechado.
              </small>
            </div>

            <div class="input-group full-width">
              <label class="input-label">
                <i class="fa-solid fa-key"></i>
                Webhook de sessão do iframe
              </label>
              <input
                class="form-input"
                type="url"
                v-model="form.sessionWebhookUrl"
                placeholder="https://api.agregador.com/api/ralph/session"
              />
              <small class="form-help-text">
                <i class="fa-solid fa-info-circle"></i>
                GET ?session= para o agregador devolver sellerId + saldo. Permite 1 iframe sem apiSecret no JavaScript.
              </small>
            </div>

            <div v-if="!editingClient" class="input-group full-width">
              <div class="info-box">
                <i class="fa-solid fa-info-circle"></i>
                <div>
                  <strong>API Key e Secret serão gerados automaticamente</strong>
                  <p>Após criar o cliente, você poderá visualizar e copiar as chaves.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="saveClient"
            :disabled="saving"
          >
            <i v-if="saving" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-save"></i>
            {{ 
              saving ? 'Salvando...' : 
              (editingClient ? 'Atualizar' : 'Salvar') 
            }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="reset()"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de visualização de chaves -->
    <div v-if="showKeysModal" class="modal-overlay" @click.self="closeKeysModal">
      <div class="modal-content keys-modal">
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-key"></i>
            Credenciais API - {{ selectedClient?.name }}
          </h3>
          <button type="button" class="btn-close" @click="closeKeysModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="credentials-warning">
            <i class="fa-solid fa-exclamation-triangle"></i>
            <p><strong>Importante:</strong> Guarde estas credenciais com segurança. A API Secret não será exibida novamente.</p>
          </div>

          <div class="credential-field">
            <label class="credential-label">
              <i class="fa-solid fa-key"></i>
              API Key
            </label>
            <div class="credential-value">
              <code>{{ selectedClient?.apiKey }}</code>
              <button 
                type="button" 
                class="btn-copy" 
                @click="copyToClipboard(selectedClient?.apiKey)"
              >
                <i class="fa-solid fa-copy"></i>
                Copiar
              </button>
            </div>
          </div>

          <div class="credential-field">
            <label class="credential-label">
              <i class="fa-solid fa-lock"></i>
              API Secret
            </label>
            <div class="credential-value">
              <code>{{ selectedClient?.apiSecret }}</code>
              <button 
                type="button" 
                class="btn-copy" 
                @click="copyToClipboard(selectedClient?.apiSecret)"
              >
                <i class="fa-solid fa-copy"></i>
                Copiar
              </button>
            </div>
          </div>

          <div class="usage-instructions">
            <h4><i class="fa-solid fa-code"></i> Como usar:</h4>
            <pre><code>// No arquivo .env do cliente Laravel:
LOTTERY_API_KEY={{ selectedClient?.apiKey }}
LOTTERY_API_SECRET={{ selectedClient?.apiSecret }}
LOTTERY_API_URL=https://api.ralphsys.com/
LOTTERY_IFRAME_URL=https://masterbixo.com</code></pre>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn-primary" @click="closeKeysModal">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import MultiSelect from "@/components/utils/MultiSelect.vue";

export default {
  name: "ViewApiClients",
  components: { MultiSelect },
  data() {
    return {
      // UUID da região padrão "online"
      DEFAULT_ONLINE_REGION_ID: "9b4e0e64-3157-474d-b385-33cc084efcdf",
      ONLINE_BANCA_UUID: "e9c624bd-8403-4b8c-9ba1-ca4954833e93",
      quickFilter: "",
      selectedTab: 1,
      loading: true,
      saving: false,
      clients: [],
      regions: [],
      bancas: [],
      lotteries: [],
      selectedRegions: [],
      selectedLotteryDetails: [],
      showKeysModal: false,
      editingClient: null,
      selectedClient: null,
      filters: {
        active: ""
      },
      form: {
        name: "",
        domain: "",
        active: true,
        defaultRegionId: "9b4e0e64-3157-474d-b385-33cc084efcdf",
        logoUrl: "",
        walletWebhookUrl: "",
        resultWebhookUrl: "",
        sessionWebhookUrl: ""
      }
    };
  },
  watch: {
    selectedRegions: {
      deep: true,
      handler() {
        this.ensureDefaultRegionInScope();
      }
    },
    availableRegions() {
      this.ensureDefaultRegionInScope();
    }
  },
  computed: {
    defaultRegionOptions() {
      if ((this.selectedRegions || []).length > 0) {
        return this.selectedRegions;
      }
      return this.availableRegions || [];
    },

    availableRegions() {
      const onlineRegionId = this.normalizeGuid(this.DEFAULT_ONLINE_REGION_ID);
      const onlineBancaId = this.normalizeGuid(this.ONLINE_BANCA_UUID);
      const bancaNameById = {};
      (this.bancas || []).forEach(banca => {
        const id = this.normalizeGuid(banca.id || banca.Id);
        if (id) bancaNameById[id] = banca.name || banca.Name || "";
      });

      return (this.regions || [])
        .filter(region => {
          const regionId = this.normalizeGuid(region.id || region.Id);
          const bancaId = this.normalizeGuid(region.bancaId || region.BancaId);
          const isOnline =
            regionId === onlineRegionId ||
            bancaId === onlineBancaId ||
            `${region.bancaName || region.BancaName || ""} ${region.name || region.Name || ""}`
              .toUpperCase()
              .includes("ONLINE");
          const isActive = region.isActive !== false && region.IsActive !== false;
          return isActive || isOnline;
        })
        .map(region => {
          const regionId = region.id || region.Id;
          const bancaId = this.normalizeGuid(region.bancaId || region.BancaId);
          const regionName = region.name || region.Name || "Sem nome";
          const bancaName =
            region.bancaName ||
            region.BancaName ||
            bancaNameById[bancaId] ||
            "";
          const isOnline =
            this.normalizeGuid(regionId) === onlineRegionId ||
            bancaId === onlineBancaId;
          const hasDescarga = !!(region.descargaRegionId || region.DescargaRegionId);
          let label;
          if (isOnline) {
            label = `ONLINE — ${regionName}`;
          } else if (bancaName) {
            label = `${bancaName} — ${regionName}`;
          } else {
            label = regionName;
          }
          if (hasDescarga && !isOnline) {
            label = `${label} · com descarga`;
          }
          return {
            id: regionId,
            name: label,
            isOnline,
            hasDescarga
          };
        })
        .sort((a, b) => {
          const aOnline = String(a.name).startsWith("ONLINE");
          const bOnline = String(b.name).startsWith("ONLINE");
          if (aOnline && !bOnline) return -1;
          if (!aOnline && bOnline) return 1;
          return a.name.localeCompare(b.name, "pt-BR");
        });
    },

    availableLotteryDetails() {
      const options = [];

      (this.lotteries || []).forEach(lottery => {
        const lotteryName = lottery.name || lottery.Name || "";
        const details = lottery.details || lottery.Details || [];

        details.forEach(detail => {
          const detailId = detail.id || detail.Id;
          const detailDescription = detail.description || detail.Description || "";
          if (!detailId) return;

          options.push({
            id: detailId,
            name: `${lotteryName} - ${detailDescription}`.trim()
          });
        });
      });

      return options.sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    },

    filteredClients() {
      let filtered = this.clients;

      // Filtro rápido por nome ou domínio
      if (this.quickFilter) {
        filtered = filtered.filter(client => 
          client.name.toUpperCase().includes(this.quickFilter.toUpperCase()) ||
          client.domain.toUpperCase().includes(this.quickFilter.toUpperCase())
        );
      }

      // Filtro por status
      if (this.filters.active !== "") {
        const isActive = this.filters.active === "true";
        filtered = filtered.filter(client => client.active === isActive);
      }

      return filtered;
    }
  },
  async mounted() {
    this.loading = true;
    try {
      await Promise.all([
        this.loadClients(false),
        this.loadRegions(),
        this.loadBancas(),
        this.loadLotteries()
      ]);
    } catch (error) {
      toast.error(handleErrorMessage(error));
    } finally {
      this.loading = false;
    }
  },
  methods: {
    unwrapList(payload) {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.result)) return payload.result;
      if (Array.isArray(payload?.items)) return payload.items;
      return [];
    },

    async loadBancas() {
      try {
        const response = await mainService.searchBancas({});
        this.bancas = this.unwrapList(response?.data);
      } catch (error) {
        console.error("Erro ao carregar bancas:", error);
        this.bancas = [];
      }
    },

    async loadRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = this.unwrapList(response?.data);
        await this.ensureOnlineRegionInList();
      } catch (error) {
        console.error("Erro ao carregar regiões:", error);
        this.regions = [];
      }
    },

    async ensureOnlineRegionInList() {
      const onlineId = this.normalizeGuid(this.DEFAULT_ONLINE_REGION_ID);
      const exists = (this.regions || []).some(
        region => this.normalizeGuid(region.id || region.Id) === onlineId
      );
      if (exists) return;
      try {
        const response = await mainService.getRegionById(this.DEFAULT_ONLINE_REGION_ID);
        const region = response?.data?.data || response?.data?.result || response?.data;
        if (region && (region.id || region.Id)) {
          this.regions = [...this.regions, region];
        }
      } catch (error) {
        console.warn("Região online não encontrada pelo ID padrão:", error);
      }
    },

    selectOnlyOnlineRegion() {
      const online = this.availableRegions.find(
        region => this.normalizeGuid(region.id) === this.normalizeGuid(this.DEFAULT_ONLINE_REGION_ID)
      ) || this.availableRegions.find(region => String(region.name).toUpperCase().includes("ONLINE"));

      if (!online) {
        toast.error("Região online não encontrada. Verifique se ela existe no cadastro de regiões.");
        return;
      }
      this.selectedRegions = [online];
      this.form.defaultRegionId = online.id;
    },

    emptyClientForm() {
      return {
        name: "",
        domain: "",
        active: true,
        defaultRegionId: this.DEFAULT_ONLINE_REGION_ID,
        logoUrl: "",
        walletWebhookUrl: "",
        resultWebhookUrl: "",
        sessionWebhookUrl: ""
      };
    },

    ensureDefaultRegionInScope() {
      const options = this.defaultRegionOptions || [];
      if (options.length === 0) {
        this.form.defaultRegionId = this.DEFAULT_ONLINE_REGION_ID;
        return;
      }

      const current = this.normalizeGuid(this.form.defaultRegionId);
      const stillValid = options.some(
        region => this.normalizeGuid(region.id) === current
      );
      if (!stillValid) {
        const online = options.find(
          region => this.normalizeGuid(region.id) === this.normalizeGuid(this.DEFAULT_ONLINE_REGION_ID)
        );
        this.form.defaultRegionId = (online || options[0]).id;
      }
    },

    async loadLotteries() {
      try {
        const response = await mainService.searchLotteries();
        const data = response?.data;
        this.lotteries = Array.isArray(data) ? data : (data?.data || []);
      } catch (error) {
        console.error("Erro ao carregar loterias:", error);
        this.lotteries = [];
      }
    },

    formatScopeCount(ids, singular, plural, emptyLabel) {
      const count = Array.isArray(ids) ? ids.length : 0;
      if (count === 0) return emptyLabel;
      return `${count} ${count === 1 ? singular : plural}`;
    },

    mapSelectedScopeIds(selectedItems) {
      return (selectedItems || [])
        .map(item => item?.id)
        .filter(id => !!id);
    },

    normalizeGuid(value) {
      return String(value || "").trim().toLowerCase();
    },

    restoreSelectedScopes(client) {
      const regionIds = (client?.allowedRegionIds || []).map(id => this.normalizeGuid(id));
      const lotteryDetailIds = (client?.allowedLotteryDetailIds || []).map(id => this.normalizeGuid(id));

      this.selectedRegions = this.availableRegions.filter(region =>
        regionIds.includes(this.normalizeGuid(region.id))
      );

      this.selectedLotteryDetails = this.availableLotteryDetails.filter(detail =>
        lotteryDetailIds.includes(this.normalizeGuid(detail.id))
      );
    },

    async loadClients(manageLoading = true) {
      if (manageLoading) {
        this.loading = true;
      }
      try {
        const response = await mainService.searchApiClients();
        console.log("📋 Resposta da API:", response);
        
        if (response && response.data) {
          // A resposta da API já retorna o array diretamente
          if (Array.isArray(response.data)) {
            this.clients = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            // Fallback caso a resposta venha aninhada
            this.clients = response.data.data;
          } else {
            // Se não for array, inicializar como array vazio
            console.warn("⚠️ Resposta não é um array:", response.data);
            this.clients = [];
          }
        } else {
          this.clients = [];
        }
      } catch (error) {
        console.error("❌ Erro ao carregar clientes:", error);
        console.error("📋 Detalhes do erro:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        toast.error("Erro ao carregar clientes: " + handleErrorMessage(error));
      } finally {
        if (manageLoading) {
          this.loading = false;
        }
      }
    },

    createClient() {
      this.editingClient = null;
      this.form = this.emptyClientForm();
      this.selectedRegions = [];
      this.selectedLotteryDetails = [];
      this.ensureDefaultRegionInScope();
      this.selectedTab = 2;
    },

    editClient(client) {
      this.editingClient = client;
      this.form = {
        name: client.name,
        domain: client.domain,
        active: client.active,
        defaultRegionId: client.defaultRegionId || this.DEFAULT_ONLINE_REGION_ID,
        logoUrl: client.logoUrl || "",
        walletWebhookUrl: client.walletWebhookUrl || "",
        resultWebhookUrl: client.resultWebhookUrl || "",
        sessionWebhookUrl: client.sessionWebhookUrl || ""
      };
      this.restoreSelectedScopes(client);
      this.ensureDefaultRegionInScope();
      this.selectedTab = 2;
    },

    reset() {
      this.editingClient = null;
      this.form = this.emptyClientForm();
      this.selectedRegions = [];
      this.selectedLotteryDetails = [];
      this.selectedTab = 1;
    },

    async searchClients() {
      await this.loadClients();
    },

    clearFilters() {
      this.filters = {
        active: ""
      };
      this.quickFilter = "";
      this.searchClients();
    },

    async saveClient() {
      if (this.saving) return;

      // Validação de campos obrigatórios
      if (!this.form.name || !this.form.name.trim()) {
        toast.error("Por favor, preencha o nome do cliente!");
        return;
      }

      if (!this.form.domain || !this.form.domain.trim()) {
        toast.error("Por favor, preencha o domínio!");
        return;
      }

      // Validar formato do domínio
      let domain = this.form.domain.trim().toLowerCase();
      
      // Remover http:// ou https:// se o usuário digitou
      domain = domain.replace(/^https?:\/\//i, '');
      
      // Remover barra no final se existir
      domain = domain.replace(/\/+$/, '');
      
      const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
      if (!domainPattern.test(domain)) {
        toast.error("Por favor, digite um domínio válido (ex: exemplo.com)!");
        return;
      }

      this.ensureDefaultRegionInScope();
      const allowedRegionIds = this.mapSelectedScopeIds(this.selectedRegions);
      const allowedLotteryDetailIds = this.mapSelectedScopeIds(this.selectedLotteryDetails);
      const defaultRegionId = this.form.defaultRegionId || this.DEFAULT_ONLINE_REGION_ID;

      if (allowedRegionIds.length > 0) {
        const defaultNormalized = this.normalizeGuid(defaultRegionId);
        const defaultInScope = allowedRegionIds.some(
          id => this.normalizeGuid(id) === defaultNormalized
        );
        if (!defaultInScope) {
          toast.error("A região padrão das apostas deve estar entre as regiões permitidas.");
          return;
        }
      }

      const dataToSend = {
        name: this.form.name.trim(),
        domain: domain,
        active: this.form.active ?? true,
        defaultRegionId,
        logoUrl: this.form.logoUrl?.trim() || null,
        walletWebhookUrl: this.form.walletWebhookUrl?.trim() || null,
        resultWebhookUrl: this.form.resultWebhookUrl?.trim() || null,
        sessionWebhookUrl: this.form.sessionWebhookUrl?.trim() || null,
        allowedRegionIds,
        allowedLotteryDetailIds
      };

      try {
        this.saving = true;

        let response;
        if (this.editingClient) {
          response = await mainService.updateApiClient({
            id: this.editingClient.id,
            ...dataToSend
          });
        } else {
          response = await mainService.insertApiClient(dataToSend);
        }

        if (response.data) {
          toast.success(
            this.editingClient 
              ? "Cliente atualizado com sucesso!" 
              : "Cliente criado com sucesso!"
          );
          
          // Se foi criação, mostrar as chaves
          if (!this.editingClient && response.data.apiKey) {
            this.selectedClient = response.data;
            this.showKeysModal = true;
          }
          
          this.reset();
          await this.loadClients();
        }
      } catch (error) {
        console.error("❌ Erro ao salvar cliente:", error);
        console.error("📋 Dados do formulário:", {
          name: this.form.name,
          domain: this.form.domain,
          active: this.form.active
        });
        console.error("📤 Dados enviados:", dataToSend);
        console.error("📋 Response data:", error.response?.data);
        console.error("📋 Status:", error.response?.status);
        
        // Mostrar mensagens de erro mais específicas
        if (error.response?.data?.errors) {
          const errorMessages = Object.values(error.response.data.errors).flat();
          toast.error("Erro de validação: " + errorMessages.join(", "));
        } else {
          toast.error("Erro ao salvar cliente: " + handleErrorMessage(error));
        }
      } finally {
        this.saving = false;
      }
    },

    async regenerateKeys(client) {
      if (!confirm(`Tem certeza que deseja regenerar as chaves do cliente "${client.name}"?\n\nAtenção: As chaves antigas deixarão de funcionar!`)) {
        return;
      }

      try {
        const response = await mainService.regenerateApiClientKeys(client.id);
        if (response.data) {
          toast.success("Chaves regeneradas com sucesso!");
          this.selectedClient = response.data;
          this.showKeysModal = true;
          await this.loadClients();
        }
      } catch (error) {
        toast.error("Erro ao regenerar chaves: " + handleErrorMessage(error));
      }
    },

    async deleteClient(client) {
      if (!confirm(`Tem certeza que deseja excluir o cliente "${client.name}"?\n\nEsta ação não pode ser desfeita!`)) {
        return;
      }

      try {
        await mainService.deleteApiClient(client.id);
        toast.success("Cliente excluído com sucesso!");
        await this.loadClients();
      } catch (error) {
        toast.error("Erro ao excluir cliente: " + handleErrorMessage(error));
      }
    },

    showApiKey(client) {
      this.selectedClient = client;
      this.showKeysModal = true;
    },

    closeKeysModal() {
      this.showKeysModal = false;
      this.selectedClient = null;
    },

    copyApiKey(apiKey) {
      if (!apiKey) {
        toast.error("API Key não disponível");
        return;
      }
      this.copyToClipboard(apiKey);
      toast.success("API Key copiada!");
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Copiado para a área de transferência!");
      } catch (error) {
        // Fallback para navegadores antigos
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Copiado para a área de transferência!");
      }
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    handleImageError(event) {
      event.target.style.display = 'none';
      const placeholder = event.target.nextElementSibling || event.target.parentElement.querySelector('.logo-placeholder');
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    },

    handlePreviewError(event) {
      event.target.style.display = 'none';
    },

  }
};
</script>

<style scoped>
.scope-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.1);
  color: var(--primary-color, #3b82f6);
  font-size: 0.8rem;
  font-weight: 600;
}

.multiselect-container {
  width: 100%;
}

/* Container principal */
.api-clients-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
}

/* Cabeçalho */
.page-header {
  margin-bottom: 2rem;
  text-align: center;
}

.page-title {
  color: var(--text-primary, #1e293b);
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.page-title i {
  color: var(--accent-color, #3b82f6);
  font-size: 1.75rem;
}

.page-subtitle {
  color: var(--text-secondary, #64748b);
  font-size: 1.1rem;
  margin: 0;
}

/* Tabs */
.tabs-container {
  margin-bottom: 2rem;
}

.modern-tabs {
  display: flex;
  gap: 0.5rem;
  border-bottom: 2px solid var(--border-color, #e2e8f0);
}

.tab-button {
  background: transparent;
  border: none;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  color: var(--text-secondary, #64748b);
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tab-button:hover {
  color: var(--primary-color, #3b82f6);
  background: var(--bg-secondary, #f8fafc);
}

.tab-button.active {
  color: var(--primary-color, #3b82f6);
  border-bottom-color: var(--primary-color, #3b82f6);
}

.tab-plus-icon {
  font-size: 0.9rem;
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--accent-color, #3b82f6);
  font-size: 1.1rem;
}

/* Tab Content */
.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Table Card */
.table-card {
  background: var(--card-background, #ffffff);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid var(--border-color, #e2e8f0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.search-container {
  flex: 1;
}

.search-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 1rem;
  color: var(--text-secondary, #64748b);
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 2px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: var(--input-background, #ffffff);
  color: var(--text-primary, #1e293b);
}

.search-input:focus {
  outline: none;
  border-color: var(--accent-color, #3b82f6);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Filtros */
.filters-section {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-primary, #1e293b);
  font-size: 0.9rem;
}

.filter-select {
  padding: 0.5rem 1rem;
  border: 2px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  font-size: 0.95rem;
  background: var(--input-background, #ffffff);
  color: var(--text-primary, #1e293b);
  cursor: pointer;
}

.filter-select:focus {
  outline: none;
  border-color: var(--accent-color, #3b82f6);
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
}

/* Botões */
.btn-primary {
  background: var(--primary-color, #3b82f6);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-primary:hover {
  background: var(--primary-color-dark, #2563eb);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: var(--bg-secondary, #f8fafc);
  color: var(--text-primary, #1e293b);
  border: 2px solid var(--border-color, #e2e8f0);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary:hover {
  background: var(--border-color, #e2e8f0);
}

/* Tabela */
.table-container {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table thead {
  background: var(--bg-secondary, #f8fafc);
}

.modern-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary, #1e293b);
  border-bottom: 2px solid var(--border-color, #e2e8f0);
}

.modern-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  color: var(--text-primary, #1e293b);
}

.modern-table tbody tr:hover {
  background: var(--bg-secondary, #f8fafc);
}

.table-row {
  transition: background-color 0.2s ease;
}

.no-data {
  text-align: center;
}

.no-data-message {
  padding: 3rem;
  color: var(--text-secondary, #64748b);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.no-data-message i {
  font-size: 3rem;
  opacity: 0.5;
}

/* Client Logo */
.logo-column {
  width: 80px;
}

.client-logo {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  margin: 0 auto;
}

.logo-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid var(--border-color, #e2e8f0);
  background: var(--bg-secondary, #f8fafc);
  padding: 4px;
}

.logo-placeholder {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary, #f8fafc);
  border: 2px dashed var(--border-color, #e2e8f0);
  border-radius: 8px;
  color: var(--text-secondary, #64748b);
  font-size: 1.5rem;
}

/* Client Info */
.client-info {
  display: flex;
  justify-content: center;
  align-items: center;
}

.client-name {
  font-weight: 500;
}

/* Domain Link */
.domain-link {
  color: var(--primary-color, #3b82f6);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s ease;
}

.domain-link:hover {
  text-decoration: underline;
  color: var(--primary-color-dark, #2563eb);
}

/* API Key Cell */
.api-key-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.api-key-preview {
  font-family: monospace;
  font-size: 0.85rem;
  color: var(--text-secondary, #64748b);
}

.btn-icon {
  background: transparent;
  border: none;
  color: var(--text-secondary, #64748b);
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  background: var(--bg-secondary, #f8fafc);
  color: var(--primary-color, #3b82f6);
}

/* Action Buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

.btn-action {
  background: transparent;
  border: none;
  color: var(--text-secondary, #64748b);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-action:hover {
  background: var(--bg-secondary, #f8fafc);
}

.btn-edit:hover {
  color: #10b981;
}

.btn-regenerate:hover {
  color: #f59e0b;
}

.btn-delete:hover {
  color: #ef4444;
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.badge-success {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.badge-danger {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.text-muted {
  color: var(--text-secondary, #64748b);
}

/* Form Card */
.form-card {
  background: var(--card-background, #ffffff);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid var(--border-color, #e2e8f0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-header {
  margin-bottom: 2rem;
}

.form-title {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: var(--text-primary, #1e293b);
}

.form-title i {
  color: var(--accent-color, #3b82f6);
  margin-right: 0.5rem;
}

.form-subtitle {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary, #64748b);
  font-weight: normal;
}

.form-body {
  margin-bottom: 2rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-group.full-width {
  grid-column: 1 / -1;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary, #1e293b);
}

.input-label.required::after {
  content: "*";
  color: #ef4444;
  margin-left: 0.25rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: var(--input-background, #ffffff);
  color: var(--text-primary, #1e293b);
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-color, #3b82f6);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.scope-quick-actions {
  display: flex;
  gap: 8px;
  margin: 0 0 10px;
}

.form-help-text {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.4;
}

.form-help-text i {
  color: var(--accent-primary);
  font-size: 0.875rem;
}

.info-box {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 8px;
  border-left: 4px solid var(--accent-color, #3b82f6);
}

.info-box i {
  color: var(--accent-color, #3b82f6);
  font-size: 1.5rem;
}

.info-box p {
  margin: 0.5rem 0 0 0;
  color: var(--text-secondary, #64748b);
}

.logo-preview {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--bg-secondary, #f8fafc);
  border-radius: 8px;
  border: 1px solid var(--border-color, #e2e8f0);
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  max-width: 200px;
  max-height: 100px;
  object-fit: contain;
  border-radius: 4px;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color, #e2e8f0);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: var(--card-background, #ffffff);
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.keys-modal {
  max-width: 700px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.modal-header h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary, #1e293b);
}

.btn-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary, #64748b);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: var(--bg-secondary, #f8fafc);
  color: var(--text-primary, #1e293b);
}

.modal-body {
  padding: 1.5rem;
}

.credentials-warning {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(245, 158, 11, 0.1);
  border-radius: 8px;
  border-left: 4px solid #f59e0b;
  margin-bottom: 1.5rem;
}

.credentials-warning i {
  color: #f59e0b;
  font-size: 1.5rem;
}

.credentials-warning p {
  margin: 0;
  color: var(--text-primary, #1e293b);
}

.credential-field {
  margin-bottom: 1.5rem;
}

.credential-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-primary, #1e293b);
}

.credential-value {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.credential-value code {
  flex: 1;
  padding: 0.75rem 1rem;
  background: var(--bg-secondary, #f8fafc);
  border: 2px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  font-family: monospace;
  font-size: 0.9rem;
  word-break: break-all;
  color: var(--text-primary, #1e293b);
}

.btn-copy {
  background: var(--primary-color, #3b82f6);
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-copy:hover {
  background: var(--primary-color-dark, #2563eb);
}

.usage-instructions {
  margin-top: 2rem;
  padding: 1rem;
  background: var(--bg-secondary, #f8fafc);
  border-radius: 8px;
}

.usage-instructions h4 {
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary, #1e293b);
}

.usage-instructions pre {
  margin: 0;
  padding: 1rem;
  background: var(--card-background, #ffffff);
  border-radius: 4px;
  overflow-x: auto;
}

.usage-instructions code {
  font-family: monospace;
  font-size: 0.85rem;
  color: var(--text-primary, #1e293b);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid var(--border-color, #e2e8f0);
}

/* Responsividade */
@media (max-width: 768px) {
  .api-clients-container {
    padding: 1rem;
  }

  .table-header {
    flex-direction: column;
    align-items: stretch;
  }

  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-actions {
    width: 100%;
  }

  .filter-actions .btn-secondary {
    flex: 1;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .modal-content {
    margin: 1rem;
  }

  .modern-table {
    font-size: 0.85rem;
  }

  .modern-table th,
  .modern-table td {
    padding: 0.5rem;
  }
}
</style>
